const SESSION_KEY = "cuo_bilt_portal_session";
const DOMAIN_GATE_KEY = "cuo_bilt_portal_domain_gate";

export type BiltRole = "partner" | "admin";

const COMEDYUO_SUFFIXES = ["@comedyuo.com", "@comedyuo.co"];
const BILT_SUFFIXES = ["@bilt.com", "@biltrewards.com"];

export function isComedyuoEmail(email: string): boolean {
  const normalized = (email || "").trim().toLowerCase();
  return COMEDYUO_SUFFIXES.some((suffix) => normalized.endsWith(suffix));
}

export function isBiltPartnerEmail(email: string): boolean {
  const normalized = (email || "").trim().toLowerCase();
  return BILT_SUFFIXES.some((suffix) => normalized.endsWith(suffix));
}

/** ComedyUO staff or BILT partner work emails can walk in. */
export function isAllowedBiltPortalEmail(email: string): boolean {
  return isComedyuoEmail(email) || isBiltPartnerEmail(email);
}

export function roleForAllowedEmail(email: string): BiltRole {
  return isComedyuoEmail(email) ? "admin" : "partner";
}

export function getBiltSessionToken(): string | null {
  try {
    return sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

export function storeBiltSessionToken(token: string): void {
  try {
    sessionStorage.setItem(SESSION_KEY, token);
  } catch {
    // ignore
  }
}

export function clearBiltSessionToken(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(DOMAIN_GATE_KEY);
  } catch {
    // ignore
  }
}

/** Domain-gate session (no magic link). Works with local sheet preview when API is down. */
export function storeDomainGateSession(email: string, role?: BiltRole): void {
  const normalized = email.trim().toLowerCase();
  if (!isAllowedBiltPortalEmail(normalized)) return;
  try {
    sessionStorage.setItem(
      DOMAIN_GATE_KEY,
      JSON.stringify({
        email: normalized,
        role: role || roleForAllowedEmail(normalized),
      })
    );
  } catch {
    // ignore
  }
}

export function getDomainGateSession(): { email: string; role: BiltRole } | null {
  try {
    const raw = sessionStorage.getItem(DOMAIN_GATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { email?: string; role?: BiltRole };
    if (!parsed?.email || !isAllowedBiltPortalEmail(parsed.email)) return null;
    return {
      email: parsed.email,
      role: parsed.role === "admin" || isComedyuoEmail(parsed.email) ? "admin" : "partner",
    };
  } catch {
    return null;
  }
}

/** @deprecated use storeDomainGateSession */
export function storeLocalComedyuoPreview(email: string, role: BiltRole = "partner"): void {
  storeDomainGateSession(email, role);
}

/** @deprecated use getDomainGateSession */
export function getLocalComedyuoPreview(): { email: string; role: BiltRole } | null {
  return getDomainGateSession();
}

function apiBase(): string {
  return import.meta.env.VITE_API_BASE_URL || "";
}

async function parseJson(res: Response): Promise<Record<string, unknown>> {
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export async function biltFetch<T = unknown>(
  path: string,
  options: RequestInit = {}
): Promise<{ ok: boolean; status: number; data: T; error?: string }> {
  const isFormData =
    typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> | undefined),
  };
  // Let the browser set multipart boundaries; JSON still needs the header.
  if (!isFormData && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  const token = getBiltSessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${apiBase()}bilt-portal/${path.replace(/^\//, "")}`, {
      ...options,
      headers,
    });
  } catch {
    return {
      ok: false,
      status: 0,
      data: {} as T,
      error: "Could not reach the BILT portal API.",
    };
  }

  const data = (await parseJson(res)) as T;
  if (!res.ok) {
    const payload = data as { error?: string; detail?: unknown };
    const detail =
      typeof payload.detail === "string"
        ? payload.detail
        : Array.isArray(payload.detail)
          ? payload.detail.map(String).join(" ")
          : "";
    const err =
      payload.error ||
      detail ||
      (res.status === 401 ? "Please sign in again." : `Request failed (${res.status}).`);
    return { ok: false, status: res.status, data, error: err };
  }
  return { ok: true, status: res.status, data };
}

/** Immediate walk-in for allowed domains (no magic link email). */
export async function requestBiltAccess(email: string) {
  return biltFetch<{
    message?: string;
    session_token?: string;
    email?: string;
    role?: BiltRole;
    error?: string;
  }>("auth/request/", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function verifyBiltMagic(token: string) {
  return biltFetch<{
    session_token?: string;
    email?: string;
    role?: BiltRole;
    error?: string;
  }>("auth/verify/", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}
